import { OrgPicker } from '@/components/OrgPicker/OrgPicker'
import {
  ClusterRevisionForm,
  type ClusterRevisionFormType,
  RevisionFormMode,
} from '@/features/ClusterConfigs/components/ClusterRevisionForm/ClusterRevisionForm'
import { getClusterRevisionFormSchema } from '@/features/ClusterConfigs/components/ClusterRevisionForm/schema'
import { VStack } from '@chakra-ui/react'
import type {
  FieldApi,
  ReactFormExtendedApi,
  StandardSchemaV1,
} from '@tanstack/react-form'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

export const getNewClusterFormSchema = (t: TFunction) =>
  getClusterRevisionFormSchema(t).extend({
    organizationId: z.string().min(1, String(t('validation.required'))),
  })

type FormValues = z.infer<ReturnType<typeof getNewClusterFormSchema>>

// Tanstack form does not provide a nice way for typing a form
export type NewClusterFormType = ReactFormExtendedApi<
  FormValues,
  undefined,
  StandardSchemaV1<FormValues, unknown>,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  StandardSchemaV1<FormValues, unknown>,
  undefined,
  undefined,
  undefined
>

export function NewClusterForm({
  form,
}: Readonly<{ form: NewClusterFormType }>) {
  const { t } = useTranslation()

  return (
    <form style={{ width: '100%' }}>
      <VStack w="full" gap={4}>
        <ClusterRevisionForm
          mode={RevisionFormMode.NEW_CLUSTER}
          form={form as unknown as ClusterRevisionFormType}
        >
          <form.Field name="organizationId">
            {(field) => {
              const { errorMessage, isInvalid } = getFieldMeta(field)
              return (
                <OrgPicker
                  id="organization-id"
                  label={String(
                    t('newClusterForm.fields.organizationId.label'),
                  )}
                  value={field.state.value}
                  onChange={(orgId) => field.handleChange(orgId)}
                  onBlur={field.handleBlur}
                  error={isInvalid ? errorMessage : undefined}
                />
              )
            }}
          </form.Field>
        </ClusterRevisionForm>
      </VStack>
    </form>
  )
}

// @ts-expect-error FieldApi expects 12 arguments
function getFieldMeta(field: FieldApi<FormValues>): {
  errorMessage?: string
  isInvalid: boolean
} {
  const errorMessage = field.state.meta.errors[0]?.message
  const isInvalid = field.state.meta.isTouched && !!errorMessage

  return { errorMessage, isInvalid }
}
